import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../services/auth-service';

interface User {
  id: string;
  username?: string;
  email?: string;
  name?: string;
  picture?: string;
  avatar?: string;
  bio?: string;
  preferences?: string[];
  contactEmail?: string;
}

interface CompleteProfileProps {
  user: User;
  onSubmit: (profileData: any) => Promise<void>;
}

export default function CompleteProfile({ user, onSubmit }: CompleteProfileProps) {
  const [username, setUsername] = useState(user.username || user.name || '');
  const [bio, setBio] = useState(user.bio || '');
  const [contactEmail, setContactEmail] = useState(user.contactEmail || user.email || '');
  const [preferences, setPreferences] = useState<string[]>(user.preferences || []);
  const [avatar, setAvatar] = useState(user.picture || user.avatar || '');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Available preferences options
  const preferenceOptions = [
    'Technology',
    'Gaming',
    'Music',
    'Art',
    'Sports',
    'Travel',
    'Food',
    'Fashion',
    'Books',
    'Movies'
  ];

  const handlePreferenceToggle = (preference: string) => {
    setPreferences(prev => 
      prev.includes(preference) 
        ? prev.filter(p => p !== preference) 
        : [...prev, preference]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await onSubmit({ 
        username, 
        bio, 
        contactEmail, 
        preferences,
        avatar
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      setLoading(false);
    }
  };

  // If user already has a username, redirect to profile
  useEffect(() => {
    if (user.username) {
      navigate('/profile');
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full mx-auto p-6 bg-card rounded-lg shadow-lg border border-border">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-foreground">Complete Your Profile</h2>
          <p className="text-muted-foreground mt-2">
            Tell us a bit more about yourself to get started
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Avatar Preview */}
          {avatar && (
            <div className="flex justify-center mb-4">
              <img 
                src={avatar} 
                alt="Profile" 
                className="w-20 h-20 rounded-full object-cover border-2 border-primary"
              />
            </div>
          )}
          
          {/* Username */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Username *
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              placeholder="Enter your username"
            />
          </div>
          
          {/* Bio */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Bio
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              placeholder="Tell us about yourself..."
            />
          </div>
          
          {/* Contact Email */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Contact Email
            </label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
              placeholder="your.email@example.com"
            />
          </div>
          
          {/* Preferences */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Interests
            </label>
            <div className="flex flex-wrap gap-2">
              {preferenceOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handlePreferenceToggle(option)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    preferences.includes(option)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          
          {/* Submit Button */}
          <button 
            type="submit" 
            disabled={loading}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>
    </div>
  );
}